import React from 'react';
import Typography from '@material-ui/core/Typography';
import T from 'i18n-react';
import Input from '../Field/Input';
import {
  validateBoolean,
  validateHoliday,
  validateLastDayStatus,
  validateString,
  validateTime,
} from './validate';
import Select from '../Field/Select';
import PlanTermContext from './context';
import type { SetPlanTermProp, Validity } from './context';
import { calculateMinHolidays } from '../../../admin/Plans/PlanTerm/functions';

const Dismissal = () => {
  const { planTerm, setPlanTerm, isEditing, validity } =
    React.useContext(PlanTermContext);

  const [state, setState] = React.useState(() => {
    const { dismissalTime, dismissalPlace, isFreeDismissal, lastDayStatus } =
      planTerm;
    return { dismissalTime, dismissalPlace, isFreeDismissal, lastDayStatus };
  });

  const { dismissalTime, dismissalPlace, isFreeDismissal, lastDayStatus } =
    state;
  const { startDate, endDate, holidays, firstDayStatus, workingDays } =
    planTerm;

  const handleChange = (data: SetPlanTermProp, validity: Validity) => {
    setState(data);
    setPlanTerm(data, validity);
  };

  return (
    <>
      <Typography component="div">
        {T.translate('planPage.planTerms.dismissedAt', {
          place: (
            <Input
              textFieldProps={{
                value: dismissalPlace,
                onChange: (event) =>
                  handleChange(
                    (prevState) => ({
                      ...prevState,
                      dismissalPlace: event.target.value,
                    }),
                    {
                      dismissalPlace: validateString(event.target.value, true),
                    },
                  ),
                error: validity.dismissalPlace === false,
                placeholder: T.translate('planPage.planTerms.dismissalPlace'),
                required: true,
                style: { maxWidth: 150 },
              }}
              readOnly={!isEditing}
            />
          ),
          time: (
            <Input
              textFieldProps={{
                type: 'time',
                value: dismissalTime,
                placeholder: T.translate('planPage.planTerms.dismissalTime'),
                required: true,
                style: { maxWidth: 150 },
              }}
              timeProps={{
                onChange: (value) =>
                  handleChange(
                    (prevState) => ({
                      ...prevState,
                      dismissalTime: value,
                    }),
                    { dismissalTime: validateTime(value, true) },
                  ),
                onError: (reason, value) =>
                  handleChange(
                    (prevState) => ({
                      ...prevState,
                      dismissalTime: value,
                    }),
                    { dismissalTime: !reason },
                  ),
              }}
              readOnly={!isEditing}
            />
          ),
        })}
      </Typography>
      <Select
        selectProps={{
          value: isFreeDismissal == null ? '' : isFreeDismissal ? 1 : 0,
          displayEmpty: true,
          error: validity.isFreeDismissal === false,
          onChange: (event) =>
            handleChange(
              (prevState) => ({
                ...prevState,
                isFreeDismissal: event.target.value,
              }),
              { isFreeDismissal: validateBoolean(event.target.value, true) },
            ),
        }}
        menuItemsProps={[
          {
            value: '',
            disabled: true,
            children: (
              <Typography
                component="span"
                color="textSecondary"
                variant="inherit"
              >
                {T.translate('planPage.planTerms.selectFreeDismissalTime')}
              </Typography>
            ),
          },
          {
            value: 1,
            children: T.translate('planPage.planTerms.isFreeDismissalTime'),
          },
          {
            value: 0,
            children: T.translate('planPage.planTerms.isNotFreeDismissalTime'),
          },
        ]}
        readOnly={!isEditing}
      />
      <Select
        selectProps={{
          value: lastDayStatus == null ? '' : lastDayStatus,
          displayEmpty: true,
          error: validity.lastDayStatus === false,
          onChange: (event) => {
            const lastDayStatus = event.target.value;
            const minHolidays = calculateMinHolidays({
              duration: workingDays + holidays,
              firstDayStatus,
              lastDayStatus,
            });

            handleChange(
              (prevState) => ({
                ...prevState,
                lastDayStatus,
                minHolidays,
              }),
              {
                lastDayStatus: validateLastDayStatus({
                  lastDayStatus,
                }),
                holiday: validateHoliday({
                  startDate,
                  endDate,
                  holidays,
                  minHolidays,
                }),
              },
            );
          },
        }}
        menuItemsProps={[
          {
            value: '',
            disabled: true,
            children: (
              <Typography
                component="span"
                color="textSecondary"
                variant="inherit"
              >
                {T.translate('planPage.planTerms.selectLastDayStatus')}
              </Typography>
            ),
          },
          {
            value: 'scheduled',
            children: T.translate('planPage.planTerms.isScheduledLastDay'),
          },
          {
            value: 'moving',
            children: T.translate('planPage.planTerms.movingDay'),
          },
        ]}
        readOnly={!isEditing}
      />
    </>
  );
};

export default Dismissal;
